import React, { useRef, useState } from 'react';

import Button from '@deliveryhero/pd-cookbook/components/ButtonV2';
import Grid from '@deliveryhero/pd-cookbook/components/Grid';
import { GridItem } from '@deliveryhero/pd-cookbook/components/GridItem';
import IcDelete from '@deliveryhero/pd-cookbook/components/Icons/IcDelete';
import IcDownload from '@deliveryhero/pd-cookbook/components/Icons/IcDownload';
import IcErrorFilled from '@deliveryhero/pd-cookbook/components/Icons/IcErrorFilled';
import IcSuccessFilled from '@deliveryhero/pd-cookbook/components/Icons/IcSuccessFilled';
import Link from '@deliveryhero/pd-cookbook/components/Link';
import { Tag, TagVariant } from '@deliveryhero/pd-cookbook/components/Tag';
import Typography from '@deliveryhero/pd-cookbook/components/Typography';
import classNames from 'classnames';
import { buildApiUrl } from 'services/api';
import { UploadFile } from 'src/components/svg/UploadFile';
import { ValidateCsvArrayResult } from 'src/utils/validate-csv-array/types';
import { validateCsvArray } from 'src/utils/validate-csv-array/validate-csv-array';

import DrawerActions from '../DrawerActions';

import { CsvUploadProps } from './types';

import './style.scss';

export const CsvUpload = ({
  config,
  columns,
  fileHandlerCallback,
  templateEndpoint,
  handleActionClick,
  successMessageMultiple,
  successMessageSingle,
  errorMessage,
  failedUpload
}: CsvUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] =
    useState<ValidateCsvArrayResult | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (!event.target) return;

      const data = event.target.result as string; // using readAsText
      const rows = data.split('\n');

      const validationResult = validateCsvArray(
        rows,
        columns.map((column) => ({
          ...column,
          hasError: false,
          isSuccess: false
        }))
      );

      setValidationResult(validationResult);
    };

    reader.readAsText(file);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;

    if (target.files) {
      setFile(target.files[0]);
      validateFile(target.files[0]);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const {
      dataTransfer: { files }
    } = event;

    if (!files.length || files[0].type !== 'text/csv') {
      return;
    }

    setFile(files[0]);
    validateFile(files[0]);
  };

  const handleFileReset = () => {
    setFile(null);
    setValidationResult(null);
    fileHandlerCallback(null, 0);
  };

  return (
    <Grid gap={'2rem'}>
      <GridItem>
        <Typography type="paragraph-sm" color="neutral-primary">
          Please complete the template with the information below.{' '}
          {templateEndpoint && (
            <Link
              href={buildApiUrl(templateEndpoint, config?.api_host)}
              target="_blank"
            >
              Download template
            </Link>
          )}
        </Typography>
      </GridItem>
      <GridItem>
        {failedUpload && (
          <>
            <Button
              variant="primary-reversed"
              prefix={<IcDownload />}
              marginBottom="xs"
              onClick={failedUpload.handleDownloadErroneousCsv}
            >
              Download incorrect emails CSV file
            </Button>
            <Typography type="paragraph-sm" color="neutral-primary">
              {`Please add ${failedUpload.recipients} valid email addresses only`}
            </Typography>
          </>
        )}
      </GridItem>
      <GridItem>
        <div
          className={classNames('upload-container', {
            'upload-container--drag-active': isDragActive
          })}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={() => {
            setIsDragActive(true);
          }}
          onDragLeave={(e) => {
            const childContainer = e.relatedTarget;

            if (
              childContainer &&
              e.currentTarget.contains(childContainer as Node)
            ) {
              return;
            }

            setIsDragActive(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsDragActive(false);

            handleFileDrop(e);
          }}
        >
          <UploadFile />
          <input
            accept=".csv"
            type="file"
            onChange={handleFileInput}
            // to fix onChange not firing when uploaded file is deleted
            onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) =>
              ((event.target as HTMLInputElement).value = '')
            }
            data-testid="fileUpload"
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <Typography type="paragraph-sm" color="neutral-primary">
            Drag and drop your file here or{' '}
            <Typography type="paragraph-sm" color="interaction-primary">
              select from your computer
            </Typography>
          </Typography>
        </div>
      </GridItem>
      {validationResult && file && (
        <GridItem>
          <div
            className={classNames('upload-message', {
              'upload-message--success': validationResult.isCsvValid,
              'upload-message--error': !validationResult.isCsvValid
            })}
          >
            <div
              className={classNames('upload-message__head', {
                'upload-message__head--success': validationResult.isCsvValid,
                'upload-message__head--error': !validationResult.isCsvValid
              })}
            >
              <Typography type="label-md">{file.name}</Typography>
              <div
                className="upload-message__head-delete"
                onClick={handleFileReset}
                data-testid="fileDelete"
              >
                <IcDelete
                  width="16px"
                  height="16px"
                  fill="interaction-secondary"
                />
              </div>
            </div>
            <Typography
              type="paragraph-sm"
              color="neutral-primary"
              className="upload-message__body"
            >
              {validationResult.isCsvValid ? (
                <>
                  <IcSuccessFilled fill="success" />
                  {validationResult.rows > 1
                    ? successMessageMultiple.replace(
                        '%row%',
                        validationResult.rows.toString()
                      )
                    : successMessageSingle.replace(
                        '%row%',
                        validationResult.rows.toString()
                      )}
                </>
              ) : (
                <>
                  <IcErrorFilled fill="error" />
                  {errorMessage}
                </>
              )}
            </Typography>
          </div>
        </GridItem>
      )}
      <GridItem>
        {columns.map((column, idx) => {
          return (
            <div key={idx} className="header-key">
              <Typography
                type="label-md"
                color={
                  validationResult
                    ? validationResult.headers[idx].hasError
                      ? 'error'
                      : validationResult.headers[idx].isSuccess
                        ? 'success'
                        : 'neutral-primary'
                    : 'neutral-primary'
                }
              >
                {column.label}
              </Typography>

              {validationResult ? (
                validationResult.headers[idx].hasError ? (
                  <IcErrorFilled
                    fill="error"
                    className="fl-error header-key__tag"
                  />
                ) : (
                  <IcSuccessFilled
                    fill="success"
                    className="fl-success header-key__tag"
                  />
                )
              ) : (
                column.required && (
                  <Tag
                    id={TagVariant.INFORMATION}
                    variant={TagVariant.INFORMATION}
                    label="Required"
                    className="header-key__tag"
                  />
                )
              )}
            </div>
          );
        })}
      </GridItem>
      <GridItem>
        <DrawerActions>
          <div className="csv-upload-button">
            <Button
              disabled={!validationResult?.isCsvValid}
              size="small"
              onClick={() => {
                handleActionClick && handleActionClick();
                fileHandlerCallback(
                  file,
                  (validationResult as ValidateCsvArrayResult).rows
                );
              }}
            >
              {failedUpload ? 'Upload & send gift cards' : 'Upload'}
            </Button>
          </div>
        </DrawerActions>
      </GridItem>
    </Grid>
  );
};
